/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {GenericObject} from '@ion/core/src/types/entities';
import VisionHistoryCard from './VisionHistoryCard';

export interface VisionHistoryListProps {
  events?: GenericObject[];
  eventClick?: (data: GenericObject) => void;
  isVideoAvailable?: (event: GenericObject) => boolean;
  toolTipLabel?: string;
}

const useStyles = makeStyles(() => ({
  list : {
    display       : 'flex',
    flexDirection : 'column'
  }
}));

const defaultIsVideoAvailable = () => true;

/**
 * VisionHistoryList renders the current theme's Vision history section - one
 * `VisionHistoryCard` row per past camera event. It mirrors the same
 * `event`/`eventClick`/`hasVideo` contract `VisionEventItem` already exposes
 * so a later integration pass can wire it to the exact same click and
 * video-availability behavior; `isVideoAvailable` lets that pass supply its
 * own availability check (e.g. the existing time-threshold rule), defaulting
 * to always-available when omitted.
 *
 * ```typescript
 *
 *   import {VisionHistoryList} from
 *     '@ion/dls/src/components/vision/current/VisionHistoryList';
 *   ...
 *
 *   <VisionHistoryList events={subsequentEvents}
 *     eventClick={function(event) { onIonVisionClick(event); }}
 *     isVideoAvailable={hasVideoAvailable}
 *     toolTipLabel='This Video Is No Longer Available' />
 *
 * ```
 *
 */
export const VisionHistoryList = ({
  events,
  eventClick,
  toolTipLabel,
  isVideoAvailable = defaultIsVideoAvailable
}: VisionHistoryListProps) => {
  const classes = useStyles();

  return (
    <div className={classes.list}>
      {events?.map(function(event: GenericObject, index: number) {
        const hasVideo = isVideoAvailable(event);
        return (
          <VisionHistoryCard key={event?.id ?? index}
            event={event}
            eventClick={eventClick}
            hasVideo={hasVideo}
            toolTipLabel={!hasVideo ? toolTipLabel : null}
            showDivider />
        );
      })}
    </div>
  );
};

export default React.memo(VisionHistoryList);
